/**
 * StorageService - localStorage wrapper with JSON support and namespacing
 * 
 * Handles all persistent data storage for the app including:
 * - User progress
 * - Achievements
 * - Streaks
 * - Settings
 * - Completed lessons
 */

const STORAGE_PREFIX = 'coding_app_';

class StorageService {
  constructor() {
    this.isAvailable = this._checkAvailability();
  }

  /**
   * Check if localStorage is available
   */
  _checkAvailability() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('StorageService: localStorage not available');
      return false;
    }
  }

  /**
   * Get the full key with prefix
   */
  _getKey(key) {
    return `${STORAGE_PREFIX}${key}`;
  }

  /**
   * Get a value from storage
   */
  get(key, defaultValue = null) {
    if (!this.isAvailable) return defaultValue;

    try {
      const item = localStorage.getItem(this._getKey(key));
      if (item === null) return defaultValue;
      return JSON.parse(item);
    } catch (e) {
      console.error(`StorageService: Error reading ${key}`, e);
      return defaultValue;
    }
  }

  /**
   * Set a value in storage
   */
  set(key, value) {
    if (!this.isAvailable) return false;

    try {
      localStorage.setItem(this._getKey(key), JSON.stringify(value));
      return true;
    } catch (e) {
      console.error(`StorageService: Error writing ${key}`, e);
      return false;
    }
  }

  /**
   * Remove a value from storage
   */
  remove(key) {
    if (!this.isAvailable) return false;

    try {
      localStorage.removeItem(this._getKey(key));
      return true;
    } catch (e) {
      console.error(`StorageService: Error removing ${key}`, e);
      return false;
    }
  }

  /**
   * Check if a key exists
   */
  has(key) {
    if (!this.isAvailable) return false;
    return localStorage.getItem(this._getKey(key)) !== null;
  }

  /**
   * Clear all app data
   */
  clearAll() {
    if (!this.isAvailable) return false;

    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (e) {
      console.error('StorageService: Error clearing storage', e);
      return false;
    }
  }

  // ==========================================
  // User Progress Methods
  // ==========================================

  /**
   * Get all completed lesson IDs
   */
  getCompletedLessons() {
    return this.get('completed_lessons', []);
  }

  /**
   * Mark a lesson as completed
   */
  markLessonCompleted(lessonId) {
    const completed = this.getCompletedLessons();
    if (!completed.includes(lessonId)) {
      completed.push(lessonId);
      this.set('completed_lessons', completed);
    }
    return completed;
  }

  /**
   * Check if a lesson is completed
   */
  isLessonCompleted(lessonId) {
    return this.getCompletedLessons().includes(lessonId);
  }

  /**
   * Get lesson progress (current step)
   */
  getLessonProgress(lessonId) {
    const progress = this.get('lesson_progress', {});
    return progress[lessonId] || { step: 0, completed: false };
  }

  /**
   * Save lesson progress
   */
  saveLessonProgress(lessonId, step, completed = false) {
    const progress = this.get('lesson_progress', {});
    progress[lessonId] = { step, completed, timestamp: Date.now() };
    this.set('lesson_progress', progress);
  }

  // ==========================================
  // Challenge Methods
  // ==========================================

  /**
   * Get completed challenge IDs
   */
  getCompletedChallenges() {
    return this.get('completed_challenges', []);
  }

  /**
   * Mark a challenge as completed
   */
  markChallengeCompleted(challengeId) {
    const completed = this.getCompletedChallenges();
    if (!completed.includes(challengeId)) {
      completed.push(challengeId);
      this.set('completed_challenges', completed);
    }
    return completed;
  }

  /**
   * Check if a challenge is completed
   */
  isChallengeCompleted(challengeId) {
    return this.getCompletedChallenges().includes(challengeId);
  }

  // ==========================================
  // Points & Level Methods
  // ==========================================

  /**
   * Get total points
   */
  getTotalPoints() {
    return this.get('total_points', 0);
  }

  /**
   * Add points
   */
  addPoints(points) {
    const current = this.getTotalPoints();
    const newTotal = current + points;
    this.set('total_points', newTotal);
    return newTotal;
  }

  /**
   * Set total points directly
   */
  setTotalPoints(points) {
    this.set('total_points', points);
    return points;
  }

  // ==========================================
  // Achievement/Badge Methods
  // ==========================================

  /**
   * Get unlocked badge IDs
   */
  getUnlockedBadges() {
    return this.get('unlocked_badges', []);
  }

  /**
   * Unlock a badge
   */
  unlockBadge(badgeId) {
    const unlocked = this.getUnlockedBadges();
    if (!unlocked.includes(badgeId)) {
      unlocked.push(badgeId);
      this.set('unlocked_badges', unlocked);
      
      // Store unlock timestamp
      const timestamps = this.get('badge_timestamps', {});
      timestamps[badgeId] = Date.now();
      this.set('badge_timestamps', timestamps);
    }
    return unlocked;
  }

  /**
   * Check if a badge is unlocked
   */
  isBadgeUnlocked(badgeId) {
    return this.getUnlockedBadges().includes(badgeId);
  }

  /**
   * Get badge unlock timestamp
   */
  getBadgeUnlockTime(badgeId) {
    const timestamps = this.get('badge_timestamps', {});
    return timestamps[badgeId] || null;
  }

  // ==========================================
  // Streak Methods
  // ==========================================

  /**
   * Get streak data
   */
  getStreakData() {
    return this.get('streak_data', {
      currentStreak: 0,
      longestStreak: 0,
      lastActiveDate: null,
      totalDaysActive: 0
    });
  }

  /**
   * Update streak data
   */
  updateStreakData(data) {
    const current = this.getStreakData();
    this.set('streak_data', { ...current, ...data });
  }

  /**
   * Get current streak count
   */
  getCurrentStreak() {
    return this.getStreakData().currentStreak;
  }

  /**
   * Get longest streak
   */
  getLongestStreak() {
    return this.getStreakData().longestStreak;
  }

  // ==========================================
  // Settings Methods
  // ==========================================

  /**
   * Get user settings
   */
  getSettings() {
    return this.get('user_settings', {
      darkMode: false,
      soundEnabled: true,
      hapticEnabled: true,
      fontSize: 14,
      showLineNumbers: true,
      codeTheme: 'monokai'
    });
  }

  /**
   * Get a single setting value
   */
  getSetting(key, defaultValue = null) {
    const settings = this.getSettings();
    return settings[key] !== undefined ? settings[key] : defaultValue;
  }

  /**
   * Save a single setting
   */
  saveSetting(key, value) {
    return this.updateSetting(key, value);
  }

  /**
   * Update a setting
   */
  updateSetting(key, value) {
    const settings = this.getSettings();
    settings[key] = value;
    this.set('user_settings', settings);
    return settings;
  }

  /**
   * Get dark mode preference
   */
  isDarkMode() {
    return this.getSettings().darkMode;
  }

  /**
   * Set dark mode preference
   */
  setDarkMode(enabled) {
    return this.updateSetting('darkMode', enabled);
  }

  // ==========================================
  // Onboarding Methods
  // ==========================================

  /**
   * Check if onboarding is complete
   */
  isOnboardingComplete() {
    return this.get('onboarding_complete', false);
  }

  /**
   * Mark onboarding as complete
   */
  completeOnboarding() {
    this.set('onboarding_complete', true);
    this.set('onboarding_completed_at', Date.now());
  }

  // ==========================================
  // Code Playground Methods
  // ==========================================

  /**
   * Get saved code snippets
   */
  getSavedCode() {
    return this.get('saved_code', []);
  }

  /**
   * Save a code snippet
   */
  saveCodeSnippet(snippet) {
    const saved = this.getSavedCode();
    const newSnippet = {
      id: Date.now().toString(),
      ...snippet,
      createdAt: Date.now()
    };
    saved.unshift(newSnippet);
    // Keep only last 50 snippets
    if (saved.length > 50) {
      saved.pop();
    }
    this.set('saved_code', saved);
    return newSnippet;
  }

  /**
   * Delete a saved code snippet
   */
  deleteCodeSnippet(snippetId) {
    const saved = this.getSavedCode();
    const filtered = saved.filter(s => s.id !== snippetId);
    this.set('saved_code', filtered);
    return filtered;
  }

  /**
   * Get last used code
   */
  getLastCode() {
    return this.get('last_code', '');
  }

  /**
   * Save last used code
   */
  saveLastCode(code) {
    this.set('last_code', code);
  }

  // ==========================================
  // Statistics Methods
  // ==========================================

  /**
   * Get user statistics
   */
  getStatistics() {
    return this.get('user_statistics', {
      totalLessonsCompleted: 0,
      totalChallengesCompleted: 0,
      totalCodeExecutions: 0,
      totalTimeSpent: 0,
      firstUseDate: null,
      lastActiveDate: null
    });
  }

  /**
   * Update statistics
   */
  updateStatistics(updates) {
    const stats = this.getStatistics();
    this.set('user_statistics', { ...stats, ...updates });
  }

  /**
   * Increment a statistic
   */
  incrementStatistic(key, amount = 1) {
    const stats = this.getStatistics();
    stats[key] = (stats[key] || 0) + amount;
    this.set('user_statistics', stats);
    return stats[key];
  }

  /**
   * Record activity (updates lastActiveDate)
   */
  recordActivity() {
    const stats = this.getStatistics();
    const now = Date.now();
    
    if (!stats.firstUseDate) {
      stats.firstUseDate = now;
    }
    stats.lastActiveDate = now;
    
    this.set('user_statistics', stats);
  }

  // ==========================================
  // Export/Import Methods
  // ==========================================

  /**
   * Export all user data as JSON
   */
  exportData() {
    // Get streak data and ensure lastActiveDate is set from statistics if null
    const streakData = this.getStreakData();
    const statistics = this.getStatistics();
    
    // Fix: If streakData.lastActiveDate is null, use statistics.lastActiveDate
    if (!streakData.lastActiveDate && statistics.lastActiveDate) {
      streakData.lastActiveDate = statistics.lastActiveDate;
    }
    
    const data = {
      version: '1.1',
      exportedAt: Date.now(),
      completedLessons: this.getCompletedLessons(),
      completedChallenges: this.getCompletedChallenges(),
      unlockedBadges: this.getUnlockedBadges(),
      badgeTimestamps: this.get('badge_timestamps', {}),
      totalPoints: this.getTotalPoints(),
      streakData: streakData,
      settings: this.getSettings(),
      statistics: statistics,
      savedCode: this.getSavedCode(),
      lessonProgress: this.get('lesson_progress', {}),
      onboardingComplete: this.isOnboardingComplete(),
      lastCode: this.getLastCode()
    };
    return JSON.stringify(data, null, 2);
  }

  /**
   * Import user data from JSON
   */
  importData(jsonString) {
    try {
      const data = JSON.parse(jsonString);
      
      // Basic validation
      if (!data || typeof data !== 'object') {
        console.error('StorageService: Invalid data format');
        return false;
      }
      
      // Import completed lessons
      if (data.completedLessons && Array.isArray(data.completedLessons)) {
        this.set('completed_lessons', data.completedLessons);
      }
      
      // Import completed challenges
      if (data.completedChallenges && Array.isArray(data.completedChallenges)) {
        this.set('completed_challenges', data.completedChallenges);
      }
      
      // Import unlocked badges
      if (data.unlockedBadges && Array.isArray(data.unlockedBadges)) {
        this.set('unlocked_badges', data.unlockedBadges);
      }
      
      // Import badge timestamps
      if (data.badgeTimestamps && typeof data.badgeTimestamps === 'object') {
        this.set('badge_timestamps', data.badgeTimestamps);
      }
      
      // Import total points
      if (data.totalPoints !== undefined && typeof data.totalPoints === 'number') {
        this.set('total_points', data.totalPoints);
      }
      
      // Import streak data with fix for null lastActiveDate
      if (data.streakData && typeof data.streakData === 'object') {
        const streakData = { ...data.streakData };
        
        // Fix: If lastActiveDate is null, try to get from statistics
        if (!streakData.lastActiveDate) {
          if (data.statistics && data.statistics.lastActiveDate) {
            streakData.lastActiveDate = data.statistics.lastActiveDate;
          } else {
            // If still null, set to current date to preserve streak on import
            streakData.lastActiveDate = new Date().toISOString().split('T')[0];
          }
        }
        
        this.set('streak_data', streakData);
      }
      
      // Import settings
      if (data.settings && typeof data.settings === 'object') {
        this.set('user_settings', data.settings);
      }
      
      // Import statistics
      if (data.statistics && typeof data.statistics === 'object') {
        this.set('user_statistics', data.statistics);
      }
      
      // Import saved code
      if (data.savedCode && Array.isArray(data.savedCode)) {
        this.set('saved_code', data.savedCode);
      }
      
      // Import lesson progress
      if (data.lessonProgress && typeof data.lessonProgress === 'object') {
        this.set('lesson_progress', data.lessonProgress);
      }
      
      // Import onboarding status
      if (data.onboardingComplete !== undefined) {
        this.set('onboarding_complete', data.onboardingComplete);
      }
      
      // Import last code
      if (data.lastCode !== undefined) {
        this.set('last_code', data.lastCode);
      }
      
      console.log('StorageService: Data imported successfully');
      return true;
    } catch (e) {
      console.error('StorageService: Failed to import data', e);
      return false;
    }
  }
}

// Export singleton instance
export const storageService = new StorageService();
export default storageService;